import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, Subject, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  CommonNotificationPayload,
  CommunicationItem,
  CommunicationResponse,
  HelpdeskCategory,
  PersonalNotificationPayload,
} from '../../../core/models/communication.models';
import { CONTENT_LOADING, SKIP_LOADING } from '../../../core/interceptors/loading.interceptor';

@Injectable({
  providedIn: 'root',
})
export class CommunicationService {
  private readonly http = inject(HttpClient);
  private readonly API_URL = environment.apiBaseUrl;

  private readonly ticketCreatedSource = new Subject<void>();
  readonly ticketCreated$ = this.ticketCreatedSource.asObservable();

  private readonly commonNotificationSentSource = new Subject<void>();
  readonly commonNotificationSent$ = this.commonNotificationSentSource.asObservable();

  /**
   * Retrieves common communications.
   */
  getCommonCommunications(skip = 0, limit = 10): Observable<CommunicationResponse> {
    return this.http.get<CommunicationResponse>(`${this.API_URL}/api/communications/common`, {
      params: { skip: skip.toString(), limit: limit.toString() },
      context: new HttpContext().set(CONTENT_LOADING, true),
    });
  }

  /**
   * Retrieves personal communications.
   */
  getPersonalCommunications(
    skip = 0,
    limit = 10,
    organizationId?: number,
  ): Observable<CommunicationResponse> {
    const params: Record<string, string> = {
      skip: skip.toString(),
      limit: limit.toString(),
    };
    if (organizationId !== undefined) {
      params['organization_id'] = organizationId.toString();
    }

    return this.http.get<CommunicationResponse>(`${this.API_URL}/api/communications/personal`, {
      params,
      context: new HttpContext().set(CONTENT_LOADING, true),
    });
  }

  /**
   * Retrieves helpdesk communications.
   */
  getHelpdeskCommunications(
    skip = 0,
    limit = 10,
    organizationId?: number,
    status?: 'active' | 'closed',
  ): Observable<CommunicationResponse> {
    const params: Record<string, string> = {
      skip: skip.toString(),
      limit: limit.toString(),
    };
    if (organizationId !== undefined) {
      params['organization_id'] = organizationId.toString();
    }
    if (status !== undefined) {
      params['status'] = status;
    }

    return this.http.get<CommunicationResponse>(`${this.API_URL}/api/communications/helpdesk`, {
      params,
      context: new HttpContext().set(CONTENT_LOADING, true),
    });
  }

  /**
   * Sends a common notification to all organizations.
   */
  sendCommonNotification(payload: CommonNotificationPayload): Observable<void> {
    return this.http
      .post<void>(`${this.API_URL}/api/communications/common`, payload, {
        context: new HttpContext().set(SKIP_LOADING, true),
      })
      .pipe(tap(() => this.commonNotificationSentSource.next()));
  }

  /**
   * Sends a personal notification to a specific organization.
   */
  sendPersonalNotification(payload: PersonalNotificationPayload): Observable<void> {
    return this.http.post<void>(`${this.API_URL}/api/communications/personal`, payload, {
      context: new HttpContext().set(CONTENT_LOADING, true),
    });
  }

  /**
   * Retrieves a single helpdesk ticket by ID.
   */
  getHelpdeskTicket(id: number): Observable<CommunicationItem> {
    return this.http.get<CommunicationItem>(
      `${this.API_URL}/api/communications/helpdesk/${id}/read`,
      {
        context: new HttpContext().set(CONTENT_LOADING, true),
      },
    );
  }

  /**
   * Sends a reply to a helpdesk ticket.
   */
  replyHelpdesk(id: number, text: string): Observable<void> {
    return this.http.post<void>(
      `${this.API_URL}/api/communications/helpdesk/${id}/reply`,
      { message_text: text },
      {
        context: new HttpContext().set(CONTENT_LOADING, true),
      },
    );
  }

  /**
   * Creates a new helpdesk ticket.
   */
  createHelpdeskTicket(payload: CommonNotificationPayload): Observable<void> {
    return this.http
      .post<void>(`${this.API_URL}/api/communications/helpdesk`, payload, {
        context: new HttpContext().set(CONTENT_LOADING, true),
      })
      .pipe(tap(() => this.ticketCreatedSource.next()));
  }

  /**
   * Retrieves helpdesk ticket categories.
   */
  getHelpdeskCategories(): Observable<HelpdeskCategory[]> {
    return this.http.get<HelpdeskCategory[]>(
      `${this.API_URL}/api/communications/helpdesk/categories`,
      { context: new HttpContext().set(SKIP_LOADING, true) },
    );
  }

  /**
   * Deletes a common communication by ID.
   */
  deleteCommonCommunication(id: number): Observable<void> {
    return this.http.delete<void>(`${this.API_URL}/api/communications/common/${id}`, {
      context: new HttpContext().set(SKIP_LOADING, true),
    });
  }
}
